// A failed orchestration does NOT report runtimeStatus "Failed". The orchestrators catch
// their own exceptions, write the reason into custom status via SetErrorStatus and return
// false, so the durable runtime sees a normal completion. Anything deciding "did this fail"
// has to consult custom status too, or it renders a failure as a success (AB#13582).

/** The phase SetErrorStatus writes when an orchestration gives up. */
const FAILED_PHASE = "Failed";

/** Minimal shape needed to judge failure - satisfied by IOrchestrationInstanceSummary. */
export interface IOrchestrationOutcome {
    runtimeStatus: string;
    failureMessage?: string | null;
    serializedOutput?: string | null;
    customStatus?: {
        currentPhase?: string;
        errorMessage?: string;
    } | null;
}

function nonBlank(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
}

/**
 * What the run's output says: a manufacturing phase returns a phase result
 * `{"success":false,"errorMessage":"..."}` (AB#13042) rather than `false`, so a withheld line
 * commit ends "Completed" with its reason only in the output (AB#14226).
 */
export function readOutput(serializedOutput: string | null | undefined): { failed: boolean; message: string | null } {
    if (!nonBlank(serializedOutput)) {
        return { failed: false, message: null };
    }
    let parsed: unknown = serializedOutput;
    try {
        parsed = JSON.parse(serializedOutput as string);
    } catch {
        // Not JSON: nothing more to read.
        return { failed: false, message: null };
    }
    if (parsed === false) {
        return { failed: true, message: null };
    }
    if (parsed && typeof parsed === "object") {
        const result = parsed as Record<string, unknown>;
        const message = result.errorMessage ?? result.ErrorMessage;
        return {
            failed: (result.success ?? result.Success) === false,
            message: typeof message === "string" ? nonBlank(message) : null,
        };
    }
    return { failed: false, message: null };
}

/**
 * The reason a run failed, preferring the durable runtime's own message over the one the
 * orchestrator recorded, then the reason in its output. Null when there is nothing worth showing.
 */
export function getFailureMessage(instance: IOrchestrationOutcome): string | null {
    return (
        nonBlank(instance.failureMessage) ??
        nonBlank(instance.customStatus?.errorMessage) ??
        readOutput(instance.serializedOutput).message
    );
}

/**
 * Whether the run failed, by any of the three signals it can arrive on. Deliberately does
 * not key off "not Completed": SetConfirmedNoSyncStatus reports a benign terminal phase for
 * an order confirmed in CS but intentionally never sent to D365, which is not a failure.
 */
export function hasFailed(instance: IOrchestrationOutcome): boolean {
    return (
        instance.runtimeStatus === FAILED_PHASE ||
        instance.customStatus?.currentPhase === FAILED_PHASE ||
        readOutput(instance.serializedOutput).failed ||
        getFailureMessage(instance) !== null
    );
}

/** The status to show the user, which is the durable status unless a failure was reported. */
export function getDisplayStatus(instance: IOrchestrationOutcome): string {
    return hasFailed(instance) ? FAILED_PHASE : instance.runtimeStatus;
}
